import { appAlert } from "./appDialogs";
import { API_ORIGIN, apiUrl } from "./apiOrigin";
// PatAppConfig.js
// UI for editing pharmacy_pwa_configurations in client_dev (push message conditionals)
// Only fields used by pushmessage app

import React, { useState, useEffect } from "react";
import "./Popup.css";
const API_BASE = apiUrl("");

// Fields from pharmacy_pwa_configurations that pushmessage uses
const PUSHMESSAGE_FIELDS = [{
  key: "primaryHex",
  label: "Primary Hex Color",
  type: "text",
  placeholder: "#000000",
  help: "Brand color hex code"
}, {
  key: "pharmacyName",
  label: "Pharmacy Name",
  type: "text",
  placeholder: "Pharmacy name",
  help: "Display name for push messages"
}, {
  key: "allow_messaging",
  label: "Allow Messaging",
  type: "checkbox",
  help: "Enable push messaging for this pharmacy"
}, {
  key: "allow_booking",
  label: "Allow Booking",
  type: "checkbox",
  help: "Enable appointment booking feature"
}, {
  key: "allow_replies",
  label: "Allow Replies",
  type: "checkbox",
  help: "Allow patients to reply to messages"
}, {
  key: "show_phone_number",
  label: "Show Phone Number",
  type: "checkbox",
  help: "Display pharmacy phone in patient app"
}, {
  key: "show_status",
  label: "Show Status",
  type: "checkbox",
  help: "Display pharmacy status/hours"
}, {
  key: "medication_reminders",
  label: "Medication Reminders",
  type: "checkbox",
  help: "Enable medication reminder push notifications"
}, {
  key: "debug",
  label: "Debug Mode",
  type: "checkbox",
  help: "Enable debug logging in patient app"
}, {
  key: "isDeleted",
  label: "Is Deleted",
  type: "checkbox",
  help: "Mark configuration as deleted"
}];
export default function PatAppConfig({
  token,
  onClose
}) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});
  const [dirty, setDirty] = useState(false);

  // Fetch current configuration
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await fetch(`${API_BASE}/api/headoffice/patient-app-config`, {
          method: "GET",
          headers: {
            "Authorization": `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        });
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        const data = await response.json();
        setConfig(data);

        // Initialize form with current values
        const initialForm = {};
        PUSHMESSAGE_FIELDS.forEach(field => {
          initialForm[field.key] = data[field.key] ?? null;
        });
        setForm(initialForm);
        setDirty(false);
      } catch (err) {
        setError(err.message || "Failed to load configuration");
        console.error("Fetch error:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchConfig();
  }, [token]);
  const handleChange = (key, value) => {
    setForm(prev => ({
      ...prev,
      [key]: value
    }));
    setDirty(true);
  };
  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      const payload = {
        _id: config._id,
        pharmacyId: config.pharmacyId,
        ...form
      };
      const response = await fetch(`${API_BASE}/api/headoffice/patient-app-config`, {
        method: "PUT",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify(payload)
      });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      const updated = await response.json();
      setConfig(updated);
      setDirty(false);
      (await appAlert("Configuration saved successfully!"));
    } catch (err) {
      setError(err.message || "Failed to save configuration");
      console.error("Save error:", err);
    } finally {
      setSaving(false);
    }
  };
  const handleReset = () => {
    // Reset form to current config values
    const resetForm = {};
    PUSHMESSAGE_FIELDS.forEach(field => {
      resetForm[field.key] = config[field.key] ?? null;
    });
    setForm(resetForm);
    setDirty(false);
  };
  if (loading) {
    return <div style={styles.container}>
        <div style={styles.panel}>
          <div style={styles.header}>
            <h2>Patient App Config (Dev)</h2>
            <button style={styles.closeBtn} onClick={onClose}>X</button>
          </div>
          <p>Loading...</p>
        </div>
      </div>;
  }
  if (!config) {
    return <div style={styles.container}>
        <div style={styles.panel}>
          <div style={styles.header}>
            <h2>Patient App Config (Dev)</h2>
            <button style={styles.closeBtn} onClick={onClose}>X</button>
          </div>
          <p style={styles.error}>No configuration found</p>
          <button style={styles.btnGhost} onClick={onClose}>← Back</button>
        </div>
      </div>;
  }
  return <div style={styles.container}>
      <div style={styles.panel}>
        <div style={styles.header}>
          <h2>Patient App Config (Dev)</h2>
          <button style={styles.closeBtn} onClick={onClose}>X</button>
        </div>

        {error && <div style={styles.errorBanner}>
            <strong>⚠️ Error:</strong> {error}
          </div>}

        <div style={styles.formContainer}>
          {PUSHMESSAGE_FIELDS.map(field => <div key={field.key} style={styles.field}>
              <label style={styles.label}>
                {field.type === "checkbox" ? <>
                    <input type="checkbox" checked={form[field.key] === true} onChange={e => handleChange(field.key, e.target.checked)} style={styles.checkbox} />
                    {field.label}
                  </> : <>
                    <div>{field.label}</div>
                    <input type={field.type} value={form[field.key] ?? ""} onChange={e => handleChange(field.key, e.target.value)} placeholder={field.placeholder} style={styles.input} />
                  </>}
              </label>
              {field.help && <div style={styles.help}>{field.help}</div>}
            </div>)}
        </div>

        <div style={styles.actions}>
          <button style={{
          ...styles.btn,
          opacity: !dirty || saving ? 0.5 : 1,
          cursor: !dirty || saving ? "not-allowed" : "pointer"
        }} onClick={handleSave} disabled={!dirty || saving}>
            {saving ? "Saving..." : "Save"}
          </button>
          <button style={{
          ...styles.btnGhost,
          marginLeft: 8
        }} onClick={handleReset} disabled={!dirty || saving}>
            Reset
          </button>
          <button style={{
          ...styles.btnGhost,
          marginLeft: 8
        }} onClick={onClose}>
            ← Back
          </button>
        </div>
      </div>
    </div>;
}
const styles = {
  container: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 10000
  },
  panel: {
    backgroundColor: "#fff",
    borderRadius: 8,
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
    width: "90%",
    maxWidth: 600,
    maxHeight: "90vh",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden"
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "16px 20px",
    borderBottom: "1px solid #F0F0F0",
    flexShrink: 0
  },
  closeBtn: {
    background: "none",
    border: "none",
    fontSize: 20,
    cursor: "pointer",
    color: "#5D5D5D",
    padding: 4
  },
  formContainer: {
    flex: 1,
    overflowY: "auto",
    padding: "20px"
  },
  field: {
    marginBottom: 16
  },
  label: {
    display: "block",
    fontSize: 14,
    fontWeight: 500,
    color: "#262626",
    marginBottom: 4
  },
  checkbox: {
    marginRight: 8,
    cursor: "pointer"
  },
  input: {
    width: "100%",
    padding: "8px 12px",
    border: "1px solid #D9D9D9",
    borderRadius: 4,
    fontSize: 14,
    fontFamily: "inherit",
    boxSizing: "border-box",
    marginTop: 4
  },
  help: {
    fontSize: 12,
    color: "#5D5D5D",
    marginTop: 4,
    fontStyle: "italic"
  },
  actions: {
    display: "flex",
    justifyContent: "flex-start",
    padding: "16px 20px",
    borderTop: "1px solid #F0F0F0",
    flexShrink: 0
  },
  btn: {
    padding: "8px 16px",
    backgroundColor: "#3b82f6",
    color: "#fff",
    border: "none",
    borderRadius: 4,
    fontSize: 14,
    fontWeight: 500,
    cursor: "pointer"
  },
  btnGhost: {
    padding: "8px 16px",
    backgroundColor: "transparent",
    color: "#3b82f6",
    border: "1px solid #3b82f6",
    borderRadius: 4,
    fontSize: 14,
    fontWeight: 500,
    cursor: "pointer"
  },
  errorBanner: {
    backgroundColor: "#FFF1F0",
    border: "1px solid #FFA39E",
    color: "#A8071A",
    padding: "12px 20px",
    margin: "0 20px 16px 20px",
    borderRadius: 4,
    fontSize: 14
  }
};
